import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import React, { useRef } from "react";
import { formatFilterDate } from "@utils/general/format-filter-date";
import ConsignmentReportFilter from "@components/filters/ConsignmentReportFilter";
import ConsignmentReportTable from "@components/report/ConsignmentReportTable";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { BsChevronBarLeft, BsChevronBarRight } from "react-icons/bs";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import useGetAllConsignmentReport from "@hooks/report/useGetAllConsignmentReport";

function ConsignmentReportPage() {
    const { page, limit, handlePageChange, handlePerRowsChange } =
        usePagination();
    const { search, setSearch } = useSearch();
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);
    const filterRef = useRef({
        from_date: undefined,
        to_date: undefined,
        customer_id: undefined,
        business_unit_id: undefined,
    });

    const {
        consignmentReportError,
        consignmentReportRefetch,
        consignmentReportFetching,
        consignmentReportPaginator,
        consignmentReportData,
    } = useGetAllConsignmentReport({
        page,
        limit,
        //@ts-ignore
        from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
        //@ts-ignore
        to_date: filterRef.current?.to_date || formatFilterDate(today),
        //@ts-ignore
        customer_id: filterRef.current?.customer_id,
        //@ts-ignore
        business_unit_id: filterRef.current?.business_unit_id,
    });

    return (
        <>
            <Search setSearch={setSearch} />
            <PageWrapper title="Consignment Report">
                <div className="grid grid-cols-8 justify-evenly">
                    <div className="col-span-8">
                        {/* @ts-ignore */}
                        <ConsignmentReportFilter
                            refetch={consignmentReportRefetch}
                            filterRef={filterRef}
                        />
                    </div>
                </div>
                {consignmentReportFetching && <LoadingSpinner />}
                <ConsignmentReportTable data={consignmentReportData} />

                {/* Pagination Controls */}
                <div className="flex items-center justify-end gap-5 mt-5">
                    {/* Rows per page */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            Rows per page
                        </span>
                        <select
                            onChange={(e) => {
                                handlePerRowsChange(+e.target.value, 1);
                            }}
                            value={limit}
                            className="border px-2 py-1 text-sm rounded"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={20}>20</option>
                            <option value={25}>25</option>
                            <option value={30}>30</option>
                        </select>
                    </div>

                    {/* Row Count Display */}
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                            {(page - 1) * limit + 1}-
                            {Math.min(
                                page * limit,
                                //@ts-ignore
                                consignmentReportPaginator?.totalItems || 0,
                            )}{" "}
                            of{" "}
                            {
                                //@ts-ignore
                                consignmentReportPaginator?.totalItems || 0
                            }
                        </span>
                    </div>

                    {/* Pagination Buttons */}
                    <div className="flex items-center gap-3">
                        {/* First Page */}
                        <button
                            disabled={page === 1}
                            onClick={() => handlePageChange(1)}
                            className={`p-2 rounded-full hover:bg-gray-100 ${
                                page === 1
                                    ? "text-gray-300"
                                    : "text-gray-600"
                            }`}
                        >
                            <BsChevronBarLeft />
                        </button>

                        {/* Previous Page */}
                        <button
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            className={`p-2 rounded-full hover:bg-gray-100 ${
                                page === 1
                                    ? "text-gray-300"
                                    : "text-gray-600"
                            }`}
                        >
                            <FaChevronLeft />
                        </button>

                        {/* Next Page */}
                        <button
                            //@ts-ignore
                            disabled={
                                page ===
                                    Math.ceil(
                                        //@ts-ignore
                                        (consignmentReportPaginator?.totalItems ||
                                            0) / limit,
                                    ) ||
                                //@ts-ignore
                                !consignmentReportData ||
                                //@ts-ignore
                                consignmentReportData?.length === 0
                            }
                            onClick={() => handlePageChange(page + 1)}
                            className={`p-2 rounded-full hover:bg-gray-100 ${
                                //@ts-ignore
                                page ===
                                    Math.ceil(
                                        //@ts-ignore
                                        (consignmentReportPaginator?.totalItems ||
                                            0) / limit,
                                    ) ||
                                //@ts-ignore
                                !consignmentReportData ||
                                //@ts-ignore
                                consignmentReportData?.length === 0
                                    ? "text-gray-300"
                                    : "text-gray-600"
                            }`}
                        >
                            <FaChevronRight />
                        </button>

                        {/* Last Page */}
                        <button
                            //@ts-ignore
                            disabled={
                                page ===
                                    Math.ceil(
                                        //@ts-ignore
                                        (consignmentReportPaginator?.totalItems ||
                                            0) / limit,
                                    ) ||
                                //@ts-ignore
                                !consignmentReportData ||
                                //@ts-ignore
                                consignmentReportData?.length === 0
                            }
                            onClick={() =>
                                handlePageChange(
                                    Math.ceil(
                                        //@ts-ignore
                                        (consignmentReportPaginator?.totalItems ||
                                            0) / limit,
                                    ),
                                )
                            }
                            className={`p-2 rounded-full hover:bg-gray-100 ${
                                //@ts-ignore
                                page ===
                                    Math.ceil(
                                        //@ts-ignore
                                        (consignmentReportPaginator?.totalItems ||
                                            0) / limit,
                                    ) ||
                                //@ts-ignore
                                !consignmentReportData ||
                                //@ts-ignore
                                consignmentReportData?.length === 0
                                    ? "text-gray-300"
                                    : "text-gray-600"
                            }`}
                        >
                            <BsChevronBarRight />
                        </button>
                    </div>
                </div>
            </PageWrapper>
        </>
    );
}

export default ConsignmentReportPage;
